import type { ChatMessage } from '../hooks/useChat'

export interface StoredConversation {
  id: string
  title: string
  updatedAt: number
  /** Copilot Studio conversation id, so a reopened chat can continue server-side. */
  conversationId?: string
  messages: ChatMessage[]
}

const PREFIX = 'contractiq.history.v1'
const MAX_CONVERSATIONS = 30
const MAX_TITLE = 70

/** History is scoped per signed-in user so shared machines never cross-leak chats. */
function storageKey(userKey: string): string {
  return `${PREFIX}:${userKey}`
}

function read(userKey: string): StoredConversation[] {
  try {
    const raw = localStorage.getItem(storageKey(userKey))
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item): item is StoredConversation =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as StoredConversation).id === 'string' &&
        Array.isArray((item as StoredConversation).messages),
    )
  } catch {
    // Storage disabled (private mode) or corrupted payload — start clean.
    return []
  }
}

function write(userKey: string, conversations: StoredConversation[]): StoredConversation[] {
  const trimmed = conversations.slice(0, MAX_CONVERSATIONS)
  try {
    localStorage.setItem(storageKey(userKey), JSON.stringify(trimmed))
  } catch {
    // Quota exceeded or storage disabled — history is best-effort, never fatal.
  }
  return trimmed
}

export function loadHistory(userKey: string): StoredConversation[] {
  return read(userKey).sort((a, b) => b.updatedAt - a.updatedAt)
}

export function saveConversation(
  userKey: string,
  conversation: StoredConversation,
): StoredConversation[] {
  const rest = read(userKey).filter((item) => item.id !== conversation.id)
  return write(userKey, [conversation, ...rest].sort((a, b) => b.updatedAt - a.updatedAt))
}

export function deleteConversation(userKey: string, id: string): StoredConversation[] {
  return write(
    userKey,
    read(userKey)
      .filter((item) => item.id !== id)
      .sort((a, b) => b.updatedAt - a.updatedAt),
  )
}

export function titleFor(messages: ChatMessage[]): string {
  const first = messages.find((message) => message.role === 'user')
  const text = first?.text.trim().replace(/\s+/g, ' ') ?? ''
  if (!text) return 'New chat'
  return text.length > MAX_TITLE ? `${text.slice(0, MAX_TITLE).trimEnd()}…` : text
}
